export class BBox {
  minLongitude: number; // West boundary
  minLatitude: number; // South boundary
  maxLongitude: number; // East boundary
  maxLatitude: number; // North boundary

  /**
   * Check if the provided thing is a BBox.
   * @param thing
   */
  static isBBox(thing: unknown): thing is BBox {
    // Hate this.
    const isBBoxPropertyTest = typeof thing === 'object' && thing !== null && 'minLongitude' in thing && 'minLatitude' in thing && 'maxLongitude' in thing && 'maxLatitude' in thing;

    return isBBoxPropertyTest;
  }

  static fromBboxArray(bbox: number[]): BBox {
    if (bbox.length !== 4) {
      throw new Error(`Invalid bbox array length: ${bbox.length}`);
    }

    const [minLongitude, minLatitude, maxLongitude, maxLatitude] = bbox;

    if (
      minLongitude === undefined
      || minLatitude === undefined
      || maxLongitude === undefined
      || maxLatitude === undefined
    ) {
      throw new Error('Invalid bbox array values');
    }

    return new BBox(minLongitude, minLatitude, maxLongitude, maxLatitude);
  }

  constructor(
    minLongitude: number | string,
    minLatitude: number | string,
    maxLongitude: number | string,
    maxLatitude: number | string,
  ) {
    this.minLongitude = this.ensureNumber(minLongitude);
    this.minLatitude = this.ensureNumber(minLatitude);
    this.maxLongitude = this.ensureNumber(maxLongitude);
    this.maxLatitude = this.ensureNumber(maxLatitude);

    this.validate();
  }

  getGeoJsonBBox(): [number, number, number, number] {
    return [
      this.minLongitude,
      this.minLatitude,
      this.maxLongitude,
      this.maxLatitude,
    ];
  }

  getAsBBoxString(): string {
    return `${this.minLongitude},${this.minLatitude},${this.maxLongitude},${this.maxLatitude}`;
  }

  /**
   * Return as a latitude,longitude string.
   */
  getAsPolygonString(latitudeFirst = true): string {
    let bottomLeft = [this.minLatitude, this.minLongitude];
    let topLeft = [this.maxLatitude, this.minLongitude];
    let topRight = [this.maxLatitude, this.maxLongitude];
    let bottomRight = [this.minLatitude, this.maxLongitude];

    if (!latitudeFirst) {
      bottomLeft = bottomLeft.reverse();
      topLeft = topLeft.reverse();
      topRight = topRight.reverse();
      bottomRight = bottomRight.reverse();
    }

    return [
      bottomLeft.join(','),
      topLeft.join(','),
      topRight.join(','),
      bottomRight.join(','),
      bottomLeft.join(','), // Closing the polygon
    ].join(',');
  }

  validate(): void {
    if (!this.isValid()) {
      throw new Error(
        `Invalid BBox: minLongitude=${this.minLongitude}, minLatitude=${this.minLatitude}, maxLongitude=${this.maxLongitude}, maxLatitude=${this.maxLatitude}`,
      );
    }
  }

  isValid(): boolean {
    if (
      this.minLongitude >= this.maxLongitude
      || this.minLatitude >= this.maxLatitude
    ) {
      return false;
    }

    if (
      this.minLongitude < -180
      || this.maxLongitude > 180
      || this.minLatitude < -90
      || this.maxLatitude > 90
    ) {
      return false;
    }

    return true;
  }

  private ensureNumber(value: number | string): number {
    const number = typeof value === 'string' ? Number.parseFloat(value) : value;

    if (Number.isNaN(number)) {
      throw new TypeError(`Invalid number: ${value}`);
    }

    return number;
  }
}
